import { Component, AfterViewInit, ElementRef, ViewChildren, QueryList, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/envs/environnement';
import { SeoService } from '../services/seo.service';

interface RolePreview {
  id: string;
  label: string;
  icon: string;
  tagline: string;
  features: string[];
}

@Component({
  selector: 'app-site-externe',
  templateUrl: './site-externe.component.html',
  styleUrls: ['./site-externe.component.scss'],
})
export class SiteExterneComponent implements AfterViewInit, OnDestroy {

  // ── Hero sector selector ─────────────────────────────────────────────────
  sectorDropdownOpen = false;
  selectedSector = 'industrie';

  sectorOptions = [
    { id: 'hopital',      label: 'Hôpitaux & Cliniques',    icon: 'pi pi-heart' },
    { id: 'industrie',    label: 'Industries & Usines',      icon: 'pi pi-cog' },
    { id: 'collectivite', label: 'Collectivités & Mairies',  icon: 'pi pi-building' },
  ];

  get selectedSectorOption() {
    return this.sectorOptions.find(s => s.id === this.selectedSector) ?? this.sectorOptions[0];
  }

  toggleSectorDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.sectorDropdownOpen = !this.sectorDropdownOpen;
  }

  selectSector(id: string): void {
    this.selectedSector = id;
    this.sectorDropdownOpen = false;
  }

  goToSelectedSector(): void {
    this.router.navigate(['/public/solutions', this.selectedSector]);
  }

  @HostListener('document:click')
  closeSectorDropdown(): void {
    this.sectorDropdownOpen = false;
  }

  // ── Roles tabs ───────────────────────────────────────────────────────────
  activeRole = 'technicien';

  rolePreviews: RolePreview[] = [
    {
      id: 'technicien',
      label: 'Technicien / Agent',
      icon: 'pi pi-wrench',
      tagline: 'Exécutez, scannez, documentez',
      features: [
        'Liste d\'interventions personnalisée du jour',
        'Scan QR code pour accéder à la fiche machine',
        'Checklists de procédures guidées étape par étape',
        'Ajout de photos et compte rendu depuis le terrain',
        'Consultation de l\'historique complet de la machine',
        'Notifications push sur les nouvelles affectations',
      ],
    },
    {
      id: 'gestionnaire',
      label: 'Gestionnaire',
      icon: 'pi pi-chart-line',
      tagline: 'Planifiez, pilotez, mesurez',
      features: [
        'Tableau de bord KPIs en temps réel (MTTR, MTBF, disponibilité)',
        'Planification des maintenances préventives automatisée',
        'Assignation des interventions selon compétences et disponibilité',
        'Calendrier interactif de toutes les interventions',
        'Gestion multi-sites depuis un espace unique',
        'Création et attribution de rôles personnalisés',
      ],
    },
    {
      id: 'daf',
      label: 'DAF',
      icon: 'pi pi-calculator',
      tagline: 'Maîtrisez les coûts, justifiez chaque dépense',
      features: [
        'Tableau de bord financier : budget réel vs prévisionnel',
        'Analyse du coût total de possession par équipement',
        'Rapports de dépenses exportables PDF / Excel',
        'Suivi du ROI maintenance par site et par service',
        'Gestion des abonnements et licences utilisateurs',
        'Rapports conformes aux exigences des auditeurs',
      ],
    },
  ];

  get currentRolePreview(): RolePreview {
    return this.rolePreviews.find(r => r.id === this.activeRole) ?? this.rolePreviews[0];
  }

  setActiveRole(id: string): void {
    this.activeRole = id;
  }

  // ── Pricing & misc ───────────────────────────────────────────────────────
  pricingPlans: { name: string; price: string; features: string[]; tagline: string | null; recommended: boolean }[] = [];
  essaiJours = 90;
  faqOpenIndex: number | null = null;
  showScrollTop = false;
  showCookieConsent = false;

  @ViewChildren('scrollElement') scrollElements!: QueryList<ElementRef>;
  private observer?: IntersectionObserver;

  @HostListener('window:scroll')
  onScroll() {
    this.showScrollTop = window.scrollY > 400;
  }

  sectors = [
    { icon: 'pi pi-cog',     label: 'Industries',       id: 'industrie' },
    { icon: 'pi pi-heart',   label: 'Hôpitaux',         id: 'hopital' },
    { icon: 'pi pi-building',label: 'Collectivités',    id: 'collectivite' },
    { icon: 'pi pi-home',    label: 'Hôtels',           id: '' },
    { icon: 'pi pi-leaf',    label: 'Agroalimentaire',  id: '' },
    { icon: 'pi pi-server',  label: 'Data Centers',     id: '' },
  ];

  stats = [
    { number: '+99%', label: 'Disponibilité équipements' },
    { number: '-45%', label: 'Coûts de maintenance' },
    { number: '+60%', label: 'Productivité équipes' },
  ];

  techFeatures = [
    {
      icon: 'pi pi-shield',
      title: 'Chiffrement de bout en bout',
      description: 'Toutes vos données sont protégées par un chiffrement AES-256. Les communications transitent en SSL/TLS.',
      badge: 'AES-256 · SSL/TLS',
      highlight: false,
    },
    {
      icon: 'pi pi-clock',
      title: 'Disponibilité garantie 99,9%',
      description: 'Infrastructure haute disponibilité avec SLA 99,9% et sauvegardes automatiques quotidiennes.',
      badge: 'SLA 99,9%',
      highlight: false,
    },
    {
      icon: 'pi pi-link',
      title: 'API REST ouverte',
      description: 'Intégrez SGMAO à vos outils existants (ERP, SAP, RH) grâce à notre API REST documentée.',
      badge: 'REST · Webhooks',
      highlight: false,
    },
    {
      icon: 'pi pi-refresh',
      title: 'Sauvegardes automatiques',
      description: 'Vos données sont sauvegardées automatiquement chaque jour. Restauration en un clic.',
      badge: 'Backup quotidien',
      highlight: false,
    },
    {
      icon: 'pi pi-eye',
      title: 'Monitoring 24/7',
      description: 'Notre équipe surveille l\'infrastructure en continu pour détecter et résoudre tout incident.',
      badge: '24/7 monitoring',
      highlight: false,
    },
    {
      icon: 'pi pi-building',
      title: 'Déploiement sur vos serveurs',
      description: 'SGMAO peut être installé directement sur votre infrastructure (on-premise). Vos données ne quittent jamais vos locaux.',
      badge: 'On-Premise disponible',
      highlight: true,
    },
  ];

  aiFeatures = [
    { text: 'Assistance en temps réel', detail: 'Réponses instantanées aux questions techniques' },
    { text: 'Suggestions intelligentes', detail: 'Recommandations basées sur l\'historique de maintenance' },
    { text: 'Diagnostic guidé', detail: 'Aide au diagnostic des pannes étape par étape' },
    { text: 'Documentation automatique', detail: 'Génération automatique des comptes rendus et rapports' },
    { text: 'Base de connaissances', detail: 'Accès rapide aux procédures et manuels constructeurs' },
    { text: 'Apprentissage continu', detail: 'S\'améliore à chaque intervention documentée' },
  ];

  testimonials = [
    {
      content: 'SGMAO a transformé notre gestion quotidienne. Nous avons réduit nos temps d\'arrêt de 30% en seulement 3 mois.',
      author: 'Kofi Mensah',
      role: 'Directeur Technique, SONABEL',
    },
    {
      content: 'L\'interface est incroyable. Mes équipes l\'ont adoptée en une journée sans formation complexe.',
      author: 'Aminata Diallo',
      role: 'Responsable Maintenance, BuildCorp',
    },
    {
      content: 'Le support client est réactif et les fonctionnalités IA nous font gagner un temps précieux sur les diagnostics.',
      author: 'Marc Ouédraogo',
      role: 'CEO, InduSystBF',
    },
  ];

  faqItems = [
    {
      question: 'SGMAO est-il adapté aux PME comme aux grandes entreprises ?',
      answer: 'Oui, SGMAO s\'adapte à toutes les tailles d\'organisation. Nos plans flexibles permettent de commencer avec une petite équipe et d\'évoluer selon vos besoins.',
    },
    {
      question: 'Combien de temps faut-il pour mettre en place SGMAO ?',
      answer: 'La mise en place prend en moyenne 48 heures. Notre équipe vous accompagne lors de l\'onboarding : configuration, import des données et formation des équipes sont inclus.',
    },
    {
      question: 'Est-ce que mes données sont sécurisées ?',
      answer: 'Absolument. Vos données sont chiffrées de bout en bout (AES-256), hébergées sur des serveurs certifiés ISO 27001 en Europe et sauvegardées quotidiennement.',
    },
    {
      question: 'Puis-je importer mes données existantes (Excel, CSV) ?',
      answer: 'Oui, SGMAO propose des outils d\'import depuis Excel et CSV. Notre équipe support peut vous accompagner dans la migration de vos données existantes.',
    },
    {
      question: 'Puis-je créer mes propres rôles et niveaux d\'accès ?',
      answer: 'Oui, c\'est l\'une de nos fonctionnalités phares. L\'administrateur peut créer des rôles sur mesure (ex : superviseur de site, chef d\'équipe...) et définir précisément ce que chaque rôle peut voir ou modifier.',
    },
    {
      question: 'Y a-t-il une application mobile ?',
      answer: 'SGMAO est une application web responsive, accessible depuis n\'importe quel appareil (PC, tablette, smartphone) sans installation. Une application mobile native est en cours de développement.',
    },
    {
      question: 'Que se passe-t-il à la fin de mon essai gratuit ?',
      answer: 'À la fin de votre essai, vous choisissez le plan qui correspond à vos besoins. Vos données sont conservées et vous continuez sans interruption. Aucune carte bancaire n\'est requise pour démarrer.',
    },
    {
      question: 'Puis-je intégrer SGMAO à mes outils existants (ERP, SAP) ?',
      answer: 'SGMAO propose une API REST ouverte qui permet l\'intégration avec vos systèmes existants. Notre équipe technique peut vous accompagner dans les intégrations spécifiques.',
    },
  ];

  constructor(
    private router: Router,
    private seoService: SeoService,
    private http: HttpClient
  ) {
    this.initSEO();
    this.checkCookieConsent();
    this.loadPricingPlans();
    this.loadEssaiJours();
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────
  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    this.scrollElements.forEach(el => this.observer!.observe(el.nativeElement));
    this.scrollElements.changes.subscribe((list: QueryList<ElementRef>) =>
      list.forEach(el => this.observer!.observe(el.nativeElement))
    );
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  // ── Navigation ───────────────────────────────────────────────────────────
  goToLogin(): void  { this.router.navigate(['/login']); }
  goToRegister(): void { this.router.navigate(['/login']); }
  goToContact(): void { this.router.navigate(['/public/contact']); }
  goToSector(id: string): void {
    if (id) this.router.navigate(['/public/solutions', id]);
  }
  goToRole(id: string): void { this.router.navigate(['/public/roles', id]); }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  scrollToTop(): void { window.scrollTo({ top: 0, behavior: 'smooth' }); }

  // ── FAQ ──────────────────────────────────────────────────────────────────
  toggleFaq(index: number): void {
    this.faqOpenIndex = this.faqOpenIndex === index ? null : index;
  }

  // ── Pricing ──────────────────────────────────────────────────────────────
  loadPricingPlans(): void {
    this.http.get<any[]>(`${environment.baseUrl}/plans-abonnement/publics`).subscribe({
      next: plans => {
        this.pricingPlans = (plans || []).map((plan, index) => ({
          name: plan.nom,
          price: plan.prixMensuel != null ? Number(plan.prixMensuel).toLocaleString('fr-FR') : 'Sur devis',
          features: this.featuresDuPlan(plan),
          tagline: plan.description || null,
          recommended: index === 1,
        }));
      },
      error: () => {},
    });
  }

  private featuresDuPlan(plan: any): string[] {
    return [
      plan.limiteUtilisateurs != null ? `Jusqu'à ${plan.limiteUtilisateurs} utilisateurs` : 'Utilisateurs illimités',
      plan.limiteEquipements != null ? `Gestion de ${plan.limiteEquipements} équipements` : 'Équipements illimités',
    ];
  }

  loadEssaiJours(): void {
    this.http.get<any>(`${environment.baseUrl}/parametres/abonnement`).subscribe({
      next: reglages => { this.essaiJours = Number(reglages?.SUBSCRIPTION_TRIAL_DAYS ?? 90); },
      error: () => {},
    });
  }

  // ── SEO ──────────────────────────────────────────────────────────────────
  initSEO(): void {
    this.seoService.updateMetaTags(
      'SGMAO — Logiciel GMAO | Gestion de Maintenance Industrielle en Afrique',
      'Gérez vos équipements, planifiez vos maintenances et réduisez vos coûts avec SGMAO. Solution africaine déployable cloud ou on-premise. Essai gratuit 90 jours.',
      'GMAO, SGMAO, logiciel maintenance, gestion maintenance industrielle, maintenance préventive, Burkina Faso, Afrique, on-premise, CMMS'
    );
    this.seoService.updateCanonicalUrl('https://sgmao.neerelab.com/');
    this.seoService.updateOgImage(
      'https://sgmao.neerelab.com/android-chrome-512x512.png',
      'Logo SGMAO — Logiciel de Maintenance Industrielle'
    );
  }

  // ── Cookies ──────────────────────────────────────────────────────────────
  checkCookieConsent(): void {
    this.showCookieConsent = !localStorage.getItem('cookieConsent');
  }

  acceptCookies(): void {
    localStorage.setItem('cookieConsent', 'accepted');
    this.showCookieConsent = false;
  }

  refuseCookies(): void {
    localStorage.setItem('cookieConsent', 'refused');
    this.showCookieConsent = false;
  }
}
